import * as _ from "lodash"
import * as AsyncLock from "async-lock";
import { PASearch } from "../PA.Core/PASearch"
import * as hash from "object-hash";
import { CalendarUtils } from "../PA.MD.Services/CalendarUtils";


/**
// Process = "Simple"
// --
// -- Pick Dates capture the Periods
// --   No Options
// --  Generally used when daily data is available
//

// Process = "Date_Offset"
// --
// -- Pick Dates are shifted by DataOffset Specified in Options
// --  operates the same as "Simple"
// -- -3,-2,-1,0,1,2,3
//-- Retailer-Observation OffSet applied
//

// Process = "Week_Fitting"
// --
// -- Pick Dates are shifted to nearest Week defined in Option
// --
// Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday
// Reported, Reported Consumption Cycle


// Assignment Percent
// Assignment Pcnt = "100"
// --
// -- Pick records that have any overlap
// --

// Assignment Pcnt = "Percent"
// --
// -- Pick records are allocated by an overlapping date range by #days
// --
*/



export interface IVolumeFit {
    Key?: string
    Process: "Simple" | "Date_Offset" | "Week_Fitting"
    // Simple Options
    // no options

    // Date_Offset Options
    DateOffSet?: number

    // Week_Fitting Options
    // no options, retailer week fitting

    // Options
    ExtendToFullWeek?: boolean

    // 100     = 100% of overlapping
    // Percent = Partial Week Percent Overlap
    AssignmentPcnt: "100" | "Percent" | "EndingDate"
}

const VolumeFitDefault: IVolumeFit = {
    Process: "Simple",
    AssignmentPcnt: "EndingDate"
}



export interface IVolumeAlignment {
    Base: IVolumeFit
    EDLP: IVolumeFit
    Merch: IVolumeFit,
    DispShipper: IVolumeFit

    // Consumption Calendar Offset
    ConsumptionDataRetailerDateOffset?: number
    RetailerStartDayOfWeek?: number

}

const vWB_EvtAlignment_Schema = PASearch.createSimpleSchema(
    ['EVENT_ID','EVENT_NAME',
        'BASE_PROCESS',   'BASE_DATA_OFFSET_OPTION', 'BASE_WEEK_FITTING_OPTION', 'BASE_ASSIGNMENT_PERCENT',
        'EDLP_PROCESS',   'EDLP_DATA_OFFSET_OPTION', 'EDLP_WEEK_FITTING_OPTION', 'EDLP_ASSIGNMENT_PERCENT',
        'MERCH_PROCESS',  'MERCH_DATA_OFFSET_OPTION', 'MERCH_WEEK_FITTING_OPTION', 'MERCH_ASSIGNMENT_PERCENT',
        'SHIPPER_PROCESS','SHIPPER_DATA_OFFSET_OPTION', 'SHIPPER_WEEK_FITTING_OPTION', 'SHIPPER_ASSIGNMENT_PERCENT',
    ],
    ['WB_EVT_ID', 'WB_SALESORG_ID', 'WB_PLAN_ID', 'WB_CUST_ID_PLANNING'])

interface ICacheEntry {
    WB_EVT_ID: number
    WB_SALESORG_ID: number
    WB_CUST_ID_PLANNING: number
    Alignment: IVolumeAlignment
}

export class VolumeAlignmentUtils {
    private static loadLock = new AsyncLock();
    private static dctEventAlignment: {[wbEvtId: number]: ICacheEntry} = {}

    static async getEventConfig(wbEventId: number, startDt: Date, endDt: Date): Promise<IVolumeAlignment> {
        let wbEvtId = wbEventId || 0

        let lockKey = `Evt-${wbEvtId}`
        return await VolumeAlignmentUtils.loadLock.acquire<IVolumeAlignment>(lockKey, async (done) => {
            try {
                let ce: ICacheEntry
                if (wbEvtId in VolumeAlignmentUtils.dctEventAlignment) {
                    ce = VolumeAlignmentUtils.dctEventAlignment[wbEvtId]
                } else {
                    let rows: any[] = []
                    if (wbEvtId > 0)
                        rows = await PASearch.find('vWB_EvtAlignment', { where: { WB_EVT_ID: wbEvtId } }, vWB_EvtAlignment_Schema, [], 1, [])
                    let row = _.first(rows) || {}
                    let wbSalesOrgId: number = +row?.WB_SALESORG_ID || 0
                    let wbCustIdPlanningId: number = +row?.WB_CUST_ID_PLANNING || 0


                    function getVolumeFit(sType: string): IVolumeFit {
                        let processFieldName = `${sType}_PROCESS`
                        let dataOffsetOptsFieldName = `${sType}_DATA_OFFSET_OPTION`
                        let weekFitOptsFieldName = `${sType}_WEEK_FITTING_OPTION`
                        let assignmentPcntFieldName = `${sType}_ASSIGNMENT_PERCENT`

                        let process = row?.[processFieldName] || ''
                        let dataOffsetOpts = row?.[dataOffsetOptsFieldName] || ''
                        let weekFitOpts = row?.[weekFitOptsFieldName] || ''
                        let assignmentPcnt = row?.[assignmentPcntFieldName] || ''

                        let rv: IVolumeFit = {
                            Process: undefined,
                            AssignmentPcnt: undefined
                        }

                        switch (process) {
                            case "Simple":
                                rv.Process = process
                                break
                            case "Date_Offset":
                                rv.Process = process
                                let offset = +dataOffsetOpts
                                if (_.isNaN(offset))
                                    offset = 0
                                offset = Math.trunc(offset)
                                offset = Math.max(Math.min(offset, 3), -3)
                                rv.DateOffSet = offset
                                break
                            case "Week_Fitting":
                                rv.Process = process
                                break
                            default:
                                rv.Process = "Simple"
                        }

                        switch (assignmentPcnt) {
                            case "Percent":
                            case "100":
                            case "EndingDate":
                                rv.AssignmentPcnt = assignmentPcnt
                                break;
                            default:
                                rv.AssignmentPcnt = "100"
                        }
                        VolumeAlignmentUtils.setKey(rv)
                        return rv
                    }

                    // Create Cache Entry and add to dictionary
                    ce = {
                        WB_EVT_ID: wbEvtId,
                        WB_SALESORG_ID: wbSalesOrgId,
                        WB_CUST_ID_PLANNING: wbCustIdPlanningId,
                        Alignment: {
                            Base: getVolumeFit("BASE"),
                            EDLP: getVolumeFit("EDLP"),
                            Merch: getVolumeFit("MERCH"),
                            DispShipper: getVolumeFit("SHIPPER")
                        }
                    }
                    VolumeAlignmentUtils.dctEventAlignment[wbEvtId] = ce
                }

                let rv = _.clone(ce.Alignment)

                // Get the Retailer Calendar
                if (!CalendarUtils.hasLoadedRetailCalendars(ce.WB_SALESORG_ID, ce.WB_CUST_ID_PLANNING))
                    await CalendarUtils.loadRetailCalendars(ce.WB_SALESORG_ID, [ce.WB_CUST_ID_PLANNING])
                let rtlCals = CalendarUtils.getRetailCalendars(ce.WB_SALESORG_ID, [ce.WB_CUST_ID_PLANNING], startDt, endDt)
                let rtlCal = _.first(rtlCals)
                rv.ConsumptionDataRetailerDateOffset = rtlCal?.consumptionData?.dataOffset || 0
                rv.RetailerStartDayOfWeek = rtlCal?.startDayOfWeek || 1
                done(undefined, rv)
            } catch(err) {
                done(err)
            }
        })
    }

    static resetCache(): void {
        VolumeAlignmentUtils.dctEventAlignment = {}
    }

    static defaultVolumeFitSetKey(src: IVolumeFit, dflt: IVolumeFit): IVolumeFit {
        let rv: IVolumeFit = {
            Process: undefined,
            AssignmentPcnt: undefined
        }
        let process = src?.Process
        let dataOffsetOpts = src?.DateOffSet
        let assignmentPcnt = src?.AssignmentPcnt
        switch(process){
            case "Simple":
                rv.Process = process
                break
            case "Date_Offset":
                rv.Process  = process
                let offset = + dataOffsetOpts
                if(_.isNaN(offset))
                    offset = 0
                offset = Math.trunc(offset)
                offset = Math.max(Math.min(offset,3),-3)
                rv.DateOffSet = offset
                break
            case "Week_Fitting":
                rv.Process = process
                break
            default:
                rv.Process = dflt?.Process || "Simple"
        }

        if (src?.ExtendToFullWeek)
            rv.ExtendToFullWeek = true

        switch(assignmentPcnt){
            case "Percent":
            case "100":
            case "EndingDate":
                rv.AssignmentPcnt = assignmentPcnt
                break;
            default:
                rv.AssignmentPcnt = dflt?.AssignmentPcnt || "100"
        }

        VolumeAlignmentUtils.setKey(rv)

        return rv

    }

    static setKey(vf: IVolumeFit): void {
        vf.Key = hash(vf, {excludeKeys: (keyName)=> _.includes(['Key'], keyName)})
    }


    static defaultVolumeAlignmentSetKey(src: IVolumeAlignment, dfltVolumeFit: IVolumeFit): IVolumeAlignment {
        let rv: IVolumeAlignment = {
            Base: VolumeAlignmentUtils.defaultVolumeFitSetKey(src?.Base, dfltVolumeFit),
            EDLP: VolumeAlignmentUtils.defaultVolumeFitSetKey(src?.EDLP, dfltVolumeFit),
            Merch: VolumeAlignmentUtils.defaultVolumeFitSetKey(src?.Merch, dfltVolumeFit),
            DispShipper: VolumeAlignmentUtils.defaultVolumeFitSetKey(src?.DispShipper, dfltVolumeFit),
            RetailerStartDayOfWeek: src?.RetailerStartDayOfWeek,
            ConsumptionDataRetailerDateOffset: src?.ConsumptionDataRetailerDateOffset || 0,
        }

        return rv
    }

    static async createBasicConfig(wbCustId: number, wbSalesOrgId: number, startDt: Date, endDt: Date, volFit: IVolumeFit): Promise<IVolumeAlignment> {
        let rv: IVolumeAlignment = {
            Base: VolumeAlignmentUtils.defaultVolumeFitSetKey(volFit, volFit),
            EDLP: VolumeAlignmentUtils.defaultVolumeFitSetKey(volFit, volFit),
            Merch: VolumeAlignmentUtils.defaultVolumeFitSetKey(volFit, volFit),
            DispShipper: VolumeAlignmentUtils.defaultVolumeFitSetKey(volFit, volFit),
            RetailerStartDayOfWeek: 1,  // Sunday
            ConsumptionDataRetailerDateOffset: 0
        }

        if (!CalendarUtils.hasLoadedRetailCalendars(wbSalesOrgId, wbCustId))
            await CalendarUtils.loadRetailCalendars(wbSalesOrgId, [wbCustId])
        let rtlCals = CalendarUtils.getRetailCalendars(wbSalesOrgId, [wbCustId], startDt, endDt)
        let rtlCal = _.first(rtlCals)
        rv.ConsumptionDataRetailerDateOffset = rtlCal?.consumptionData?.dataOffset || 0
        rv.RetailerStartDayOfWeek = rtlCal?.startDayOfWeek || 1

        return rv
    }


}